import { useCallback, useEffect, useState } from 'react';
import { useList, useCreate, useUpdate, useDelete } from '@refinedev/core';
import {
    ClinicInfoState,
    CreateComplaintRequest,
    CreateDiagnosisRequest,
    CreateSuggestionRequest,
    UpdateComplaintRequest,
    UpdateDiagnosisRequest,
    UpdateSuggestionRequest,
    ComplaintItem,
    DiagnosisItem,
    TreatmentSuggestionItem,
    FutureSuggestionItem
} from '../types';

export const useClinicInfo = (patientId: number) => {
    const [state, setState] = useState<ClinicInfoState>({
        complaints: [],
        diagnosis: [],
        treatmentSuggestions: [],
        futureSuggestions: [],
    });

    // Fetch data for each section
    const { data: complaintsData, isLoading: isLoadingComplaints, refetch: refetchComplaints } = useList({
        resource: 'complaints',
        filters: [{ field: 'patient_id', operator: 'eq', value: patientId }],
    });

    const { data: diagnosisData, isLoading: isLoadingDiagnosis, refetch: refetchDiagnosis } = useList({
        resource: 'diagnosis',
        filters: [{ field: 'patient_id', operator: 'eq', value: patientId }],
    });

    const { data: treatmentSuggestionsData, isLoading: isLoadingTreatmentSuggestions, refetch: refetchTreatmentSuggestions } = useList({
        resource: 'treatment-suggestions',
        filters: [{ field: 'patient_id', operator: 'eq', value: patientId }],
        pagination: { pageSize: 100, mode: 'server' },
    });

    const { data: suggestionsData, isLoading: isLoadingSuggestions, refetch: refetchSuggestions } = useList({
        resource: 'future-suggestions',
        filters: [{ field: 'patient_id', operator: 'eq', value: patientId }],
    });

    // Create mutations for each section
    const { mutate: createComplaint } = useCreate();
    const { mutate: createDiagnosis } = useCreate();
    const { mutate: createTreatmentSuggestion } = useCreate();
    const { mutate: createFutureSuggestion } = useCreate();

    // Update mutations for each section
    const { mutate: updateComplaint } = useUpdate();
    const { mutate: updateDiagnosis } = useUpdate();
    const { mutate: updateTreatmentSuggestion } = useUpdate();
    const { mutate: updateFutureSuggestion } = useUpdate();

    // Delete mutations for each section
    const { mutate: deleteComplaint } = useDelete();
    const { mutate: deleteDiagnosis } = useDelete();
    const { mutate: deleteTreatmentSuggestion } = useDelete();
    const { mutate: deleteFutureSuggestion } = useDelete();

    // Update state when data changes
    useEffect(() => {
        if (complaintsData?.data && diagnosisData?.data && treatmentSuggestionsData?.data && suggestionsData?.data) {
            setState({
                complaints: complaintsData.data as unknown as ComplaintItem[],
                diagnosis: diagnosisData.data as unknown as DiagnosisItem[],
                treatmentSuggestions: treatmentSuggestionsData.data as unknown as TreatmentSuggestionItem[],
                futureSuggestions: suggestionsData.data as unknown as FutureSuggestionItem[],
            });
        }
    }, [complaintsData, diagnosisData, treatmentSuggestionsData, suggestionsData]);

    // Add item (appointmentId optional: for treatment-suggestion. For treatment-suggestion, first arg can be string or offering { id, name, price }.)
    const addItem = useCallback(
        (textOrOffering: string | { id: number; name: string; price: number }, type: 'complaint' | 'diagnosis' | 'treatment-suggestion' | 'future-suggestion', appointmentId?: number) => {
            switch (type) {
                case 'complaint':
                    createComplaint(
                        {
                            resource: 'complaints',
                            values: {
                                description: textOrOffering as string,
                                patient_id: patientId,
                            } as CreateComplaintRequest,
                        },
                        {
                            onSuccess: () => refetchComplaints(),
                        }
                    );
                    break;

                case 'diagnosis':
                    createDiagnosis(
                        {
                            resource: 'diagnosis',
                            values: {
                                details: textOrOffering as string,
                                patient_id: patientId,
                            } as CreateDiagnosisRequest,
                        },
                        {
                            onSuccess: () => refetchDiagnosis(),
                        }
                    );
                    break;

                case 'treatment-suggestion': {
                    const isOffering = typeof textOrOffering === 'object' && textOrOffering !== null && 'id' in textOrOffering;
                    const suggestion = isOffering ? (textOrOffering as { id: number; name: string; price: number }).name : (textOrOffering as string);
                    const price = isOffering ? (textOrOffering as { id: number; name: string; price: number }).price : undefined;
                    const offeringId = isOffering ? (textOrOffering as { id: number; name: string; price: number }).id : undefined;
                    createTreatmentSuggestion(
                        {
                            resource: 'treatment-suggestions',
                            values: {
                                suggestion,
                                patient_id: patientId,
                                ...(appointmentId != null && { appointment_id: appointmentId }),
                                ...(offeringId != null && { offering_id: offeringId }),
                                ...(price != null && price !== undefined && { price: Number(price) }),
                            } as CreateSuggestionRequest & { appointment_id?: number; offering_id?: number; price?: number },
                        },
                        {
                            onSuccess: () => refetchTreatmentSuggestions(),
                        }
                    );
                    break;
                }

                case 'future-suggestion':
                    createFutureSuggestion(
                        {
                            resource: 'future-suggestions',
                            values: {
                                suggestion: textOrOffering as string,
                                patient_id: patientId,
                            } as CreateSuggestionRequest,
                        },
                        {
                            onSuccess: () => refetchSuggestions(),
                        }
                    );
                    break;
            }
        },
        [patientId]
    );

    // Update item
    const updateItem = useCallback(
        (id: number, text: string, type: 'complaint' | 'diagnosis' | 'treatment-suggestion' | 'future-suggestion') => {
            switch (type) {
                case 'complaint':
                    updateComplaint(
                        {
                            resource: 'complaints',
                            id,
                            values: {
                                description: text,
                            } as UpdateComplaintRequest,
                        },
                        {
                            onSuccess: () => refetchComplaints(),
                        }
                    );
                    break;

                case 'diagnosis':
                    updateDiagnosis(
                        {
                            resource: 'diagnosis',
                            id,
                            values: {
                                details: text,
                            } as UpdateDiagnosisRequest,
                        },
                        {
                            onSuccess: () => refetchDiagnosis(),
                        }
                    );
                    break;

                case 'treatment-suggestion':
                    updateTreatmentSuggestion(
                        {
                            resource: 'treatment-suggestions',
                            id,
                            values: {
                                suggestion: text,
                            } as UpdateSuggestionRequest,
                        },
                        {
                            onSuccess: () => refetchTreatmentSuggestions(),
                        }
                    );
                    break;

                case 'future-suggestion':
                    updateFutureSuggestion(
                        {
                            resource: 'future-suggestions',
                            id,
                            values: {
                                suggestion: text,
                            } as UpdateSuggestionRequest,
                        },
                        {
                            onSuccess: () => refetchSuggestions(),
                        }
                    );
                    break;
            }
        },
        []
    );

    // Delete item
    const deleteItem = useCallback(
        (id: number, type: 'complaint' | 'diagnosis' | 'treatment-suggestion' | 'future-suggestion') => {
            switch (type) {
                case 'complaint':
                    deleteComplaint(
                        {
                            resource: 'complaints',
                            id,
                        },
                        {
                            onSuccess: () => refetchComplaints(),
                        }
                    );
                    break;

                case 'diagnosis':
                    deleteDiagnosis(
                        {
                            resource: 'diagnosis',
                            id,
                        },
                        {
                            onSuccess: () => refetchDiagnosis(),
                        }
                    );
                    break;

                case 'treatment-suggestion':
                    deleteTreatmentSuggestion(
                        {
                            resource: 'treatment-suggestions',
                            id,
                        },
                        {
                            onSuccess: () => refetchTreatmentSuggestions(),
                        }
                    );
                    break;

                case 'future-suggestion':
                    deleteFutureSuggestion(
                        {
                            resource: 'future-suggestions',
                            id,
                        },
                        {
                            onSuccess: () => refetchSuggestions(),
                        }
                    );
                    break;
            }
        },
        []
    );

    return {
        state,
        isLoading: isLoadingComplaints || isLoadingDiagnosis || isLoadingTreatmentSuggestions || isLoadingSuggestions,
        addItem,
        updateItem,
        deleteItem,
    };
};